import httpService from "./httpService";
import {
  _resetFactorsRequestGuardForTesting,
  runWithFactorsRequestGuard,
} from "./factorsRequestLimiter";

export type DiscoverFactorProductType = "basket" | "portfolio";

export interface DiscoverFactorEntry {
  t: string;
  f: number;
}

export interface DiscoverFactorsResponse {
  currentPage: number;
  currentPageNumOfItems: number;
  totalPages: number;
  totalItems: number;
  items: DiscoverFactorEntry[];
}

export interface DiscoverFactorsBatchRequest {
  providerId: number;
  productType: DiscoverFactorProductType;
  productId: number;
  from?: string;
  to?: string;
  pageSize?: number;
}

export interface DiscoverFactorsBatchResult extends DiscoverFactorsBatchRequest {
  factors: DiscoverFactorEntry[];
  /** True only when the product request failed; an empty successful series omits this. */
  failed?: true;
}

const PAGE_SIZE = 10000;
const PAGE_FETCH_CONCURRENCY = 5;
const BATCH_FETCH_CONCURRENCY = 5;

const FACTORS_CACHE_TTL_MS = 30 * 60 * 1000;
const FACTORS_CACHE_SWEEP_INTERVAL_MS = 10 * 60 * 1000;

interface FactorsCacheEntry {
  factors: DiscoverFactorEntry[];
  fetchedAt: number;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];

  const safeConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  await Promise.all(Array.from({ length: safeConcurrency }, () => worker()));
  return results;
}

class DiscoverFactorsService {
  private factorsCache = new Map<string, FactorsCacheEntry>();
  private pendingRequests = new Map<string, Promise<DiscoverFactorEntry[]>>();
  private lastSweepAt = 0;

  async getFactorsPage(
    providerId: number,
    productType: DiscoverFactorProductType,
    productId: number,
    page: number,
    from?: string,
    to?: string,
    pageSize: number = PAGE_SIZE
  ): Promise<DiscoverFactorsResponse> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const response = await runWithFactorsRequestGuard(() =>
      httpService.get<DiscoverFactorsResponse>(
        `/v3/products/factors/providers/${providerId}/${productType}/${productId}?${params.toString()}`
      )
    );
    return response.data;
  }

  async getAdminFactorsPage(
    providerId: number,
    productType: DiscoverFactorProductType,
    productId: number,
    page: number,
    from?: string,
    to?: string,
    pageSize: number = PAGE_SIZE
  ): Promise<DiscoverFactorsResponse> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const response = await runWithFactorsRequestGuard(() =>
      httpService.get<DiscoverFactorsResponse>(
        `/v3/products/factors/providers/${providerId}/${productType}/${productId}/admin?${params.toString()}`
      )
    );
    return response.data;
  }

  async getFactors(
    providerId: number,
    productType: DiscoverFactorProductType,
    productId: number,
    from?: string,
    to?: string,
    pageSize?: number,
    admin: boolean = false
  ): Promise<DiscoverFactorEntry[]> {
    if (from || to) {
      return this.fetchAllPages(providerId, productType, productId, from, to, pageSize, admin);
    }

    this.sweepExpiredEntries();

    // Admin results can differ from the public route, so namespace the cache.
    const cacheKey = `${admin ? "admin:" : ""}${providerId}-${productType}-${productId}`;
    const now = Date.now();

    const cached = this.factorsCache.get(cacheKey);
    if (cached && now - cached.fetchedAt < FACTORS_CACHE_TTL_MS) {
      return cached.factors;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending;

    const promise = this.fetchAllPages(providerId, productType, productId, undefined, undefined, pageSize, admin)
      .then((factors) => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.factorsCache.set(cacheKey, { factors, fetchedAt: Date.now() });
        }
        return factors;
      })
      .finally(() => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  private async fetchAllPages(
    providerId: number,
    productType: DiscoverFactorProductType,
    productId: number,
    from?: string,
    to?: string,
    pageSize?: number,
    admin: boolean = false
  ): Promise<DiscoverFactorEntry[]> {
    const fetchPage = (page: number) =>
      admin
        ? this.getAdminFactorsPage(providerId, productType, productId, page, from, to, pageSize)
        : this.getFactorsPage(providerId, productType, productId, page, from, to, pageSize);

    const firstPage = await fetchPage(1);
    const pages: DiscoverFactorsResponse[] = [firstPage];

    if (firstPage.totalPages > 1) {
      const remainingPageNumbers = Array.from(
        { length: firstPage.totalPages - 1 },
        (_, index) => index + 2
      );
      const remainingPages = await mapWithConcurrency(
        remainingPageNumbers,
        PAGE_FETCH_CONCURRENCY,
        (page) => fetchPage(page)
      );
      pages.push(...remainingPages);
    }

    return pages
      .flatMap((page) => page.items)
      .sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
  }

  private sweepExpiredEntries(): void {
    const now = Date.now();
    if (now - this.lastSweepAt < FACTORS_CACHE_SWEEP_INTERVAL_MS) return;
    this.lastSweepAt = now;

    for (const [key, entry] of this.factorsCache) {
      if (now - entry.fetchedAt >= FACTORS_CACHE_TTL_MS) {
        this.factorsCache.delete(key);
      }
    }
  }

  async getFactorsBatch(
    products: DiscoverFactorsBatchRequest[],
    concurrency: number = BATCH_FETCH_CONCURRENCY,
    onProgress?: (product: DiscoverFactorsBatchRequest, factors: DiscoverFactorEntry[]) => void,
    admin: boolean = false
  ): Promise<DiscoverFactorsBatchResult[]> {
    return mapWithConcurrency(products, concurrency, async (product) => {
      try {
        const factors = await this.getFactors(
          product.providerId,
          product.productType,
          product.productId,
          product.from,
          product.to,
          product.pageSize,
          admin
        );
        onProgress?.(product, factors);
        return { ...product, factors };
      } catch (error) {
        console.error(
          `Failed to load factors for ${product.productType}:${product.productId} (provider ${product.providerId}):`,
          error
        );
        onProgress?.(product, []);
        return { ...product, factors: [], failed: true };
      }
    });
  }

  _resetForTesting(): void {
    this.factorsCache.clear();
    this.pendingRequests.clear();
    this.lastSweepAt = 0;
    _resetFactorsRequestGuardForTesting();
  }
}

export const discoverFactorsService = new DiscoverFactorsService();
